import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { EnrichedUserActivity } from '../utils/activityApi';
import { formatActivityDetails, formatTimestamp, formatAggregatedCount } from '../utils/activityFormatters';
import { en } from '../translations/en';

export interface ActivityTableProps {
  activities: EnrichedUserActivity[];
  loading: boolean;
}

/**
 * Get the display label for an activity type.
 */
const getActivityTypeLabel = (activityType: string): string => {
  const labels = en.activityTimeline.activities;
  switch (activityType) {
    case 'CATALOG_CREATE':
      return labels.catalogCreate;
    case 'CATALOG_UPDATE':
      return labels.catalogUpdate;
    case 'CATALOG_DELETE':
      return labels.catalogDelete;
    case 'CATALOG_VISIBILITY_CHANGE':
      return labels.catalogVisibilityChange;
    case 'USER_DATA_EXPORT':
      return labels.userDataExport;
    default:
      return activityType;
  }
};

/**
 * Get the color for an activity type chip.
 */
const getActivityTypeColor = (
  activityType: string
): 'primary' | 'success' | 'error' | 'warning' => {
  switch (activityType) {
    case 'CATALOG_CREATE':
      return 'success';
    case 'CATALOG_UPDATE':
      return 'primary';
    case 'CATALOG_DELETE':
      return 'error';
    case 'CATALOG_VISIBILITY_CHANGE':
      return 'warning';
    case 'USER_DATA_EXPORT':
      return 'primary';
    default:
      return 'primary';
  }
};

/**
 * Card layout for a single activity — used on phone viewports.
 */
const ActivityCard: React.FC<{ activity: EnrichedUserActivity }> = ({ activity }) => {
  const theme = useTheme();
  const aggregatedText = formatAggregatedCount(activity.aggregatedCount);

  return (
    <Card variant="outlined" sx={{ mb: 1 }}>
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
          <Typography variant="caption" color="text.secondary">
            {formatTimestamp(activity.timestamp)}
          </Typography>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Chip
              label={getActivityTypeLabel(activity.activityType)}
              size="small"
              color={getActivityTypeColor(activity.activityType)}
            />
            {aggregatedText && (
              <Typography
                variant="caption"
                sx={{
                  backgroundColor: theme.palette.grey[200],
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 1,
                  fontWeight: 500,
                }}
              >
                {aggregatedText}
              </Typography>
            )}
          </Box>
        </Box>

        {activity.aliNumber ? (
          <Link
            to={`/catalog/${activity.resourceId}`}
            style={{ color: theme.palette.primary.main, textDecoration: 'none' }}
          >
            <Typography variant="body2" fontWeight={500}>
              {activity.aliNumber}
            </Typography>
          </Link>
        ) : activity.resourceId ? (
          <Typography variant="body2" color="text.secondary">[Deleted]</Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">—</Typography>
        )}

        <Typography variant="body2" color="text.secondary">
          {formatActivityDetails(activity.activityType, activity.details)}
        </Typography>

        {activity.editorAliId && (
          <Typography variant="caption" color="text.secondary">
            {en.activityTimeline.editor}: {activity.editorAliId}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * Responsive activity display component.
 *
 * On phone viewports (< 600px), renders a card-based layout that avoids
 * the 5-column table overflow problem. On tablet/desktop, renders the
 * standard table layout.
 */
export const ActivityTable: React.FC<ActivityTableProps> = ({
  activities,
  loading,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3} data-testid="activity-loading">
        <CircularProgress />
      </Box>
    );
  }

  if (activities.length === 0) {
    return (
      <Typography color="text.secondary" data-testid="activity-empty-state">
        {en.activityTimeline.noActivities}
      </Typography>
    );
  }

  if (isMobile) {
    return (
      <Box data-testid="activity-cards">
        {activities.map((activity, index) => (
          <ActivityCard
            key={`${activity.timestamp}-${index}`}
            activity={activity}
          />
        ))}
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} elevation={0} data-testid="activity-table">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Date/Time</TableCell>
            <TableCell>{en.activityTimeline.editor}</TableCell>
            <TableCell>Action</TableCell>
            <TableCell>Catalog</TableCell>
            <TableCell>Details</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {activities.map((activity, index) => {
            const aggregatedText = formatAggregatedCount(activity.aggregatedCount);

            return (
              <TableRow
                key={`${activity.timestamp}-${index}`}
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  '&:hover': { backgroundColor: theme.palette.action.hover },
                }}
              >
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatTimestamp(activity.timestamp)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {activity.editorAliId || '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      label={getActivityTypeLabel(activity.activityType)}
                      size="small"
                      color={getActivityTypeColor(activity.activityType)}
                    />
                    {aggregatedText && (
                      <Typography
                        variant="caption"
                        sx={{
                          backgroundColor: theme.palette.grey[200],
                          px: 0.75,
                          py: 0.25,
                          borderRadius: 1,
                          fontWeight: 500,
                        }}
                      >
                        {aggregatedText}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  {activity.aliNumber ? (
                    <Link
                      to={`/catalog/${activity.resourceId}`}
                      style={{
                        color: theme.palette.primary.main,
                        textDecoration: 'none',
                      }}
                    >
                      {activity.aliNumber}
                    </Link>
                  ) : activity.resourceId ? (
                    <Typography variant="body2" color="text.secondary">
                      [Deleted]
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatActivityDetails(activity.activityType, activity.details)}
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
